import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import {
  Task,
  TaskCompletion,
  useTaskCompletionStatusUpdateMutation,
  useTaskCompletionsIdsLazyQuery,
  useUsersSearchListLazyQuery,
} from 'graphql-common';
import { Option, Value, Values } from '@lib/interfaces/form';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import { TASK_REPORTING_FILTER_FORM } from '@lib/enums/localStorageKeys';
import useTaskCompletionsQueryHook from 'utils/fetch/hooks/useTaskCompletionsQueryHook';
import { getUsersLoadOptions as getUsersLoadOptionsFn } from 'utils/fetch/searchLists/usersSearchList';
import TaskCompletionsFilterFieldNames from '@lib/enums/fieldNames/taskCompletionsFilterFieldNames';
import { GetUrlParams } from '@lib/enums/urls';
import { APP_URLS } from 'constants/urls';
import useTaskReportingFilterFields, {
  TASK_REPORTING_FILTER_DEFAULT_VALUES,
} from './useTaskReportingFilterFields';
import getTaskReportingTableColumns, {
  StatusChangeHandler,
} from './getTaskReportingTableColumns';
import getTaskCompletionsFilters from './getTaskCompletionsFilters';
import useTaskReportingPeriod from './useTaskReportingPeriod';

export default function useTaskReportingActions({
  task,
  taskId,
}: {
  task?: Task;
  taskId?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [paginationParams] = usePaginationParams();

  const [selectedCompletionId, setSelectedCompletionId] = useState<
    string | null
  >(null);

  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(TASK_REPORTING_FILTER_FORM, TASK_REPORTING_FILTER_DEFAULT_VALUES);

  // Users selector
  const [fetchUsersSearchListLazyQuery] = useUsersSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getUsersLoadOptions = () =>
    getUsersLoadOptionsFn(fetchUsersSearchListLazyQuery);
  const filterFields = useTaskReportingFilterFields({ getUsersLoadOptions });

  // Period defaults + selected value
  const { defaultMinDateObj, defaultMaxDateObj, periodValue } =
    useTaskReportingPeriod({
      filterParams: filterParams as { period?: string[] },
    });

  // Build query filters and fetch
  const groupKey = task?.groupKey;
  const skipCompletions = !groupKey;
  const additionalFilters = useMemo(
    () => getTaskCompletionsFilters({ filterParams, periodValue }),
    [filterParams, periodValue],
  );

  const {
    collection,
    error: completionsError,
    firstLoading: completionsFirstLoading,
    loading: completionsLoading,
    refetch,
    fetchIds,
  } = useTaskCompletionsQueryHook({
    groupKey: groupKey || '',
    skip: skipCompletions,
    additionalFilters,
    fetchPolicy: 'network-only',
    paginationParams,
  });

  // Status update
  const [updateStatus, updateStatusResult] =
    useTaskCompletionStatusUpdateMutation({
      onCompleted: () => {
        toast({ content: t('task-result-state-successfully-updated') });
        refetch();
      },
      onError: (error) => showGraphQLErrors(error, t),
    });

  const handleStatusChange = useCallback<StatusChangeHandler>(
    (completion, status) => {
      const targetTaskId = completion.taskId || task?.id || taskId;
      if (!targetTaskId) return;
      updateStatus({
        variables: { taskId: targetTaskId, attributes: { status } },
      });
    },
    [task?.id, taskId, updateStatus],
  );

  // Filter counters
  let filterCounter = 0;
  if (_get(filterParams, [TaskCompletionsFilterFieldNames.Users, 'length'])) {
    filterCounter += 1;
  }
  if (
    _get(filterParams, [
      TaskCompletionsFilterFieldNames.ValidityHints,
      'length',
    ])
  ) {
    filterCounter += 1;
  }

  // Handlers
  const onResetFilters = () =>
    setFilterParams(TASK_REPORTING_FILTER_DEFAULT_VALUES);

  const onSubmitFilter = (values: Values) => {
    const users =
      (values?.[TaskCompletionsFilterFieldNames.Users] as Option[]) || [];
    const validityHints =
      (values?.[TaskCompletionsFilterFieldNames.ValidityHints] as Option[]) ||
      [];
    setFilterParams((prev) => ({
      ...prev,
      name: (values?.name as string | null) || null,
      [TaskCompletionsFilterFieldNames.Users]: users,
      [TaskCompletionsFilterFieldNames.ValidityHints]: validityHints,
    }));
  };

  const onPeriodChange = (value: string[]) => {
    if (Array.isArray(value) && value[0] && value[1])
      setFilterParams((prev) => ({ ...prev, period: value }));
    else setFilterParams((prev) => ({ ...prev, period: [] }));
  };

  const onViewTaskCompletionHandler = (item?: TaskCompletion) => {
    if (item?.id) setSelectedCompletionId(item.id);
  };

  const onChangeSelectedTaskCompletionHandler = (id: string) => {
    if (id) setSelectedCompletionId(id);
  };

  const onCloseTaskCompletionHandler = () => {
    setSelectedCompletionId(null);
  };

  const onCreateIntervention = (item?: TaskCompletion) => {
    navigate(
      APP_URLS.dashboard.interventions.add.getPathWithQuery({
        [GetUrlParams.AssetId]: item?.task?.assetId,
        [GetUrlParams.SiteAreaId]: item?.task?.siteAreaId,
        [GetUrlParams.SiteId]: item?.task?.siteId,
        [GetUrlParams.TaskId]: item?.task?.id,
      }),
    );
  };

  const onViewIntervention = (item?: TaskCompletion) => {
    const interventionId = _get(item, 'task.intervention.id');
    if (interventionId) {
      navigate(
        APP_URLS.dashboard.interventions.view.getDynamicPath({
          pathParts: { id: interventionId },
        }),
      );
    }
  };

  const tableColumns = useMemo(
    () =>
      getTaskReportingTableColumns({
        t,
        onViewTaskCompletionHandler,
        onStatusChange: handleStatusChange,
        isStatusUpdating: updateStatusResult.loading,
      }),
    [handleStatusChange, t, updateStatusResult.loading],
  );

  return {
    // Filter UI
    filterCounter,
    filterDefaultValues: filterParams,
    filterFields,
    minDate: defaultMinDateObj,
    maxDate: defaultMaxDateObj,
    periodValue,
    onPeriodChange,
    onResetFilters,
    onSubmitFilter,
    onCloseTaskCompletionHandler,
    onViewTaskCompletionHandler,
    onCreateIntervention,
    onViewIntervention,

    // Table UI
    tableColumns,
    tableData: collection as TaskCompletion[],
    tableLoading: completionsLoading || updateStatusResult.loading,
    tableFirstLoading: completionsFirstLoading,
    tableError: completionsError,

    selectedCompletionId,
    onChangeSelectedTaskCompletionHandler,
    fetchIds,
  };
}
